import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CommentIcon from "@mui/icons-material/Comment";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 800,
  margin: "0 auto",
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const BlogImage = styled(CardMedia)({
  height: 400,
  objectFit: "cover",
});

const MetaSection = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
}));

const AuthorSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
});

const AuthorAvatar = styled("img")({
  width: 40,
  height: 40,
  borderRadius: "50%",
  objectFit: "cover",
});

const StatsSection = styled(Box)({
  display: "flex",
  gap: 16,
  alignItems: "center",
});

const CommentSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
}));

const Comment = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: "white",
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[200]}`,
}));

const ViewBlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [blogImages, setBlogImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Fetch blog data
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}/auth/blog/${id}`);
        const blogData = response.data;

        setBlog(blogData);
        setLikes(blogData.likeCount || 0);
        setComments(blogData.comments || []);

        // Set blog images - if BlogImages array exists, use it, otherwise use the main imageUrl
        if (blogData.BlogImages && blogData.BlogImages.length > 0) {
          setBlogImages(blogData.BlogImages);
        } else if (blogData.imageUrl) {
          // If no BlogImages array, create array with single main image
          setBlogImages([{ imageUrl: blogData.imageUrl }]);
        }

        // Check if user liked this blog
        if (blogData.Likes) {
          // You might want to check if current user liked this post
          // For now, we'll just use the like count
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching blog:", error);
        setError("Failed to load blog post");
        setLoading(false);
      }
    };

    if (id) {
      fetchBlog();
    }
  }, [id, baseUrl]);

  const handleLike = async () => {
    try {
      const response = await axios.post(`${baseUrl}/auth/like`, {
        blogId: id,
      });

      if (response.data.liked) {
        setLikes(response.data.likeCount || likes + 1);
        setIsLiked(true);
      } else {
        setLikes(response.data.likeCount || likes - 1);
        setIsLiked(false);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      alert("Failed to like post");
    }
  };

  const handleBack = () => {
    navigate("/dashboard/blogs");
  };

  const handleEdit = () => {
    navigate(`/dashboard/editBlog/${id}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) {
      return "https://via.placeholder.com/800x400/4B5563/FFFFFF?text=Blog+Image";
    }

    const cleanImageUrl = imageUrl.startsWith("/")
      ? imageUrl.substring(1)
      : imageUrl;
    return `${baseUrl}/${cleanImageUrl}`;
  };

  const getCurrentImageUrl = () => {
    if (blogImages.length === 0) {
      return "https://via.placeholder.com/800x400/4B5563/FFFFFF?text=Blog+Image";
    }
    return getImageUrl(blogImages[selectedImageIndex]?.imageUrl);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleBack}>
              Back to Blogs
            </Button>
          }
          sx={{ mt: 2 }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!blog) {
    return (
      <Container maxWidth="lg">
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleBack}>
              Back to Blogs
            </Button>
          }
          sx={{ mt: 2 }}
        >
          Blog post not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header with Back Button */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Blogs
        </Button>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            {blog.title}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleEdit}>
            Edit Blog
          </Button>
        </Box>
      </Box>

      <StyledCard>
        {/* Main Blog Image */}
        <BlogImage
          component="img"
          image={getCurrentImageUrl()}
          alt={blog.title}
          onError={(e) => {
            e.target.src =
              "https://via.placeholder.com/800x400/4B5563/FFFFFF?text=Blog+Image";
          }}
        />

        {/* Image Gallery Thumbnails */}
        {blogImages.length > 1 && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              padding: 2,
              overflowX: "auto",
              backgroundColor: "#f5f5f5",
            }}
          >
            {blogImages.map((img, index) => (
              <Box
                key={index}
                onClick={() => setSelectedImageIndex(index)}
                sx={{
                  minWidth: 100,
                  height: 80,
                  cursor: "pointer",
                  border:
                    selectedImageIndex === index
                      ? "3px solid #1976d2"
                      : "2px solid transparent",
                  borderRadius: 1,
                  overflow: "hidden",
                  transition: "all 0.3s",
                  "&:hover": {
                    border: "3px solid #1976d2",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <img
                  src={getImageUrl(img.imageUrl)}
                  alt={`Thumbnail ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/100x80/4B5563/FFFFFF?text=Img";
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        <CardContent>
          {/* Meta Information */}
          <MetaSection>
            <AuthorSection>
              <AuthorAvatar
                src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=1061&q=80"
                alt={blog.author}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {blog.author}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Published on {formatDate(blog.date)}
                </Typography>
              </Box>
            </AuthorSection>

            <StatsSection>
              <Chip
                icon={<FavoriteIcon />}
                label={`${likes} ${likes === 1 ? "Like" : "Likes"}`}
                variant="outlined"
                color={isLiked ? "error" : "default"}
                onClick={handleLike}
                clickable
              />
              <Chip
                icon={<CommentIcon />}
                label={`${comments.length} ${comments.length === 1 ? "Comment" : "Comments"}`}
                variant="outlined"
              />
            </StatsSection>
          </MetaSection>

          <Divider sx={{ my: 2 }} />

          {/* Blog Content */}
          <Typography
            variant="body1"
            component="div"
            sx={{
              lineHeight: 1.8,
              fontSize: "1.1rem",
              whiteSpace: "pre-wrap",
            }}
          >
            {blog.description}
          </Typography>

          {/* Comments Section */}
          {comments.length > 0 && (
            <CommentSection>
              <Typography variant="h6" gutterBottom>
                Comments ({comments.length})
              </Typography>

              {comments.map((comment) => (
                <Comment key={comment.id}>
                  <Grid container spacing={2}>
                    <Grid item>
                      <AuthorAvatar
                        src={
                          comment.userAvatar ||
                          "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=1061&q=80"
                        }
                        alt={comment.userName}
                      />
                    </Grid>
                    <Grid item xs>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Typography variant="subtitle2" fontWeight="bold">
                          {comment.userName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatCommentDate(comment.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {comment.text}
                      </Typography>
                    </Grid>
                  </Grid>
                </Comment>
              ))}
            </CommentSection>
          )}

          {/* No Comments Message */}
          {comments.length === 0 && (
            <CommentSection>
              <Typography
                variant="body1"
                color="textSecondary"
                textAlign="center"
              >
                No comments yet. Be the first to comment!
              </Typography>
            </CommentSection>
          )}
        </CardContent>
      </StyledCard>

      {/* Action Buttons */}
      <Box
        sx={{ mt: 3, mb: 4, display: "flex", gap: 2, justifyContent: "center" }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to All Blogs
        </Button>
        <Button variant="contained" onClick={handleEdit}>
          Edit This Blog
        </Button>
      </Box>
    </Container>
  );
};

export default ViewBlogPost;
